import { Crown } from "lucide-react";
import { ProgressBar } from "../../shared/ui";

const ACCENT = "#B24BF3";

// Подсвечивает ___ в тексте
function Prompt({ text, tv }) {
  if (!text) return <span>...</span>;
  const parts = text.split("___");
  if (parts.length === 1) return <span>{text}</span>;
  return (
    <span>
      {parts[0]}
      <span style={{
        color: ACCENT,
        background: ACCENT + "22",
        borderRadius: "0.3rem",
        padding: tv ? "0 10px" : "0 6px",
        margin: "0 3px",
      }}>▢▢▢</span>
      {parts[1]}
    </span>
  );
}

export default function FibbageHost({
  question, lieCount, lieTotal,
  voteData, voteCount, voteTotal,
  result, players, tv,
}) {
  const fs = (large, small) => tv ? large : small;

  // Фаза 1: сбор лжи
  if (!voteData && !result) {
    return (
      <div className="w-full text-center animate-slide-up">
        <div style={{
          display: "inline-block", background: "#1a0040",
          border: "3px solid #000", borderRadius: "1.5rem",
          boxShadow: "5px 5px 0 #000",
          padding: tv ? "1.75rem 2.75rem" : "0.9rem 1.25rem",
          maxWidth: "92%", marginBottom: "1.25rem",
        }}>
          <div style={{ fontSize: fs("1rem","0.7rem"), color: ACCENT, opacity: 0.8, marginBottom: "0.5rem", fontFamily:"'Fredoka One',sans-serif" }}>
            🤥 Фиббедж — придумай ложь!
          </div>
          <h2 className="font-heading text-white" style={{ fontSize: fs("2.4rem","1.3rem"), lineHeight: 1.3 }}>
            <Prompt text={question?.prompt} tv={tv} />
          </h2>
          <div style={{ marginTop: "0.75rem", fontSize: fs("1rem","0.7rem"), color: "#aaa", fontFamily:"'Nunito',sans-serif" }}>
            Придумайте правдоподобную ложь!
          </div>
        </div>
        <div className="font-heading" style={{ fontSize: fs("1.25rem","0.85rem"), color: "#aaa", marginBottom: "0.75rem" }}>
          Придумали: {lieCount}/{lieTotal}
        </div>
        <ProgressBar value={lieCount} max={Math.max(lieTotal,1)} color={ACCENT} tv={tv} />
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "0.5rem", marginTop: "1.25rem" }}>
          {players.map((p, i) => (
            <div key={i} className="player-chip" style={{
              backgroundColor: p.color,
              fontSize: fs("1.2rem","0.85rem"),
              padding: tv ? "0.5rem 1.25rem" : "0.3rem 0.8rem",
            }}>{p.name}</div>
          ))}
        </div>
      </div>
    );
  }

  // Фаза 2: голосование
  if (voteData && !result) {
    return (
      <div className="w-full text-center animate-slide-up">
        <div style={{
          display: "inline-block", background: "#1a0040",
          border: "3px solid #000", borderRadius: "1.5rem",
          boxShadow: "5px 5px 0 #000",
          padding: tv ? "1.25rem 2.5rem" : "0.75rem 1rem",
          maxWidth: "92%", marginBottom: "1rem",
        }}>
          <h2 className="font-heading text-white" style={{ fontSize: fs("2rem","1.1rem"), lineHeight: 1.3 }}>
            <Prompt text={voteData.prompt} tv={tv} />
          </h2>
        </div>
        <div className="font-heading" style={{ fontSize: fs("1.1rem","0.78rem"), color: "#aaa", marginBottom: "0.75rem" }}>
          🗳️ Голосуют: {voteCount}/{voteTotal}
        </div>
        <ProgressBar value={voteCount} max={Math.max(voteTotal,1)} color={ACCENT} tv={tv} />
        <div style={{ maxWidth: tv ? "680px" : "100%", margin: "1rem auto 0", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {(voteData.options || []).map((opt, i) => (
            <div key={i} style={{
              background: "#16103a", border: "2.5px solid #000",
              borderRadius: "0.85rem", boxShadow: "3px 3px 0 #000",
              padding: tv ? "0.9rem 1.25rem" : "0.6rem 0.85rem",
              display: "flex", alignItems: "center", gap: "0.75rem", textAlign: "left",
            }}>
              <span className="font-display" style={{ color: ACCENT, fontSize: fs("1.5rem","1rem"), minWidth: "1.75rem" }}>
                {i + 1}
              </span>
              <span className="font-heading text-white" style={{ fontSize: fs("1.35rem","0.9rem"), flex: 1 }}>
                {opt.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Фаза 3: результаты
  if (result) {
    const opts   = result.options || [];
    const lies   = opts.filter(o => !o.is_truth).sort((a,b) => b.vote_count - a.vote_count);
    const truth  = opts.find(o => o.is_truth);

    const OptCard = ({ opt, rank }) => {
      const isWinner = rank === 0 && opt.vote_count > 0;
      const borderColor = isWinner ? "#ffe600" : "#ffffff18";
      return (
        <div style={{
          background: "#16103a",
          border: `2px solid ${borderColor}`,
          borderRadius: "0.75rem",
          boxShadow: isWinner ? `3px 3px 0 #ffe600` : "none",
          padding: tv ? "0.7rem 1rem" : "0.45rem 0.7rem",
          display: "flex", alignItems: "center", gap: "0.5rem",
        }}>
          {isWinner
            ? <Crown style={{ color: "#ffe600", width: fs("1.2rem","0.85rem"), height: fs("1.2rem","0.85rem"), flexShrink: 0 }} />
            : <span style={{ color: "#444", fontSize: fs("1rem","0.75rem"), minWidth: "1rem" }}>·</span>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="font-heading text-white" style={{ fontSize: fs("1.1rem","0.82rem"), lineHeight: 1.2 }}>
              {opt.text}
            </div>
            <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", marginTop: "0.2rem", alignItems: "center" }}>
              {(opt.authors || [opt.author]).filter(Boolean).map(a => {
                const ap = players.find(p => p.name === a);
                return <span key={a} className="font-heading" style={{ fontSize: fs("0.8rem","0.62rem"), color: ap?.color || "#aaa" }}>— {a}</span>;
              })}
              {opt.votes?.map(v => {
                const vp = players.find(p => p.name === v);
                return (
                  <span key={v} style={{
                    background: vp?.color || "#555", color: "#000",
                    borderRadius: "999px", padding: "0 0.4rem",
                    fontSize: fs("0.72rem","0.58rem"), fontFamily: "'Fredoka One',sans-serif",
                  }}>{v}</span>
                );
              })}
            </div>
          </div>
          <div style={{ textAlign: "center", flexShrink: 0, minWidth: fs("2rem","1.5rem") }}>
            <div className="font-display" style={{ fontSize: fs("1.5rem","1rem"), color: opt.vote_count > 0 ? "#ffe600" : "#333", lineHeight: 1 }}>
              {opt.vote_count}
            </div>
            <div style={{ fontSize: fs("0.65rem","0.5rem"), color: "#555", fontFamily: "'Fredoka One',sans-serif" }}>гол.</div>
          </div>
        </div>
      );
    };

    return (
      <div className="w-full animate-slide-up" style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>

        {/* Вопрос — компактно сверху */}
        <div style={{
          background: "#1a0040", border: "2.5px solid #000", borderRadius: "1rem",
          boxShadow: "4px 4px 0 #000",
          padding: tv ? "0.75rem 1.75rem" : "0.5rem 0.85rem",
          textAlign: "center",
        }}>
          <h2 className="font-heading text-white" style={{ fontSize: fs("1.6rem","0.9rem"), lineHeight: 1.25 }}>
            <Prompt text={result.prompt} tv={tv} />
          </h2>
        </div>

        {/* Двухколоночный layout */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: tv ? "0.85rem" : "0.55rem", alignItems: "start" }}>

          {/* ЛЕВАЯ колонка — варианты лжи */}
          <div style={{ display: "flex", flexDirection: "column", gap: tv ? "0.5rem" : "0.35rem" }}>
            <div className="font-heading" style={{ fontSize: fs("0.85rem","0.62rem"), color: "#888", marginBottom: "0.1rem", textAlign: "center" }}>
              🤥 ВАРИАНТЫ
            </div>
            {lies.map((opt, i) => <OptCard key={i} opt={opt} rank={i} />)}
            {lies.length === 0 && (
              <div style={{ color: "#444", fontSize: fs("0.9rem","0.7rem"), fontFamily: "'Nunito',sans-serif", textAlign: "center", padding: "1rem" }}>
                Никто не написал ложь
              </div>
            )}
          </div>

          {/* ПРАВАЯ колонка — правда + счёт */}
          <div style={{ display: "flex", flexDirection: "column", gap: tv ? "0.5rem" : "0.35rem" }}>
            <div className="font-heading" style={{ fontSize: fs("0.85rem","0.62rem"), color: "#888", marginBottom: "0.1rem", textAlign: "center" }}>
              ✅ ПРАВДА
            </div>

            {/* Правильный ответ */}
            <div style={{
              background: "#001a00", border: "2.5px solid #3dffa0",
              borderRadius: "0.75rem", boxShadow: "3px 3px 0 #3dffa0",
              padding: tv ? "0.7rem 1rem" : "0.45rem 0.7rem",
            }}>
              <div className="font-heading" style={{ color: "#3dffa0", fontSize: fs("1.2rem","0.85rem"), marginBottom: truth?.votes?.length ? "0.3rem" : 0 }}>
                {result.answer}
              </div>
              {result.fact && (
                <div className="font-body" style={{ color: "#888", fontSize: fs("0.8rem","0.62rem"), lineHeight: 1.35, marginBottom: truth?.votes?.length ? "0.3rem" : 0 }}>
                  {result.fact}
                </div>
              )}
              {truth?.votes?.length > 0 && (
                <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                  {truth.votes.map(v => {
                    const vp = players.find(p => p.name === v);
                    return (
                      <span key={v} style={{
                        background: vp?.color || "#3dffa0", color: "#000",
                        borderRadius: "999px", padding: "0 0.4rem",
                        fontSize: fs("0.72rem","0.58rem"), fontFamily: "'Fredoka One',sans-serif",
                      }}>{v}</span>
                    );
                  })}
                </div>
              )}
            </div>

            {result.nobody_guessed && (
              <div style={{
                background: "#1a1000", border: "2px solid #ffe600",
                borderRadius: "0.75rem", padding: tv ? "0.5rem 0.85rem" : "0.35rem 0.6rem",
                fontSize: fs("0.9rem","0.68rem"), color: "#ffe600",
                fontFamily: "'Fredoka One',sans-serif", textAlign: "center",
              }}>
                😈 Никто не угадал! +200 лжецам
              </div>
            )}

            {/* Счёт игроков */}
            <div style={{
              background: "#0d0820", border: "2px solid #ffffff15",
              borderRadius: "0.75rem", padding: tv ? "0.6rem 0.85rem" : "0.4rem 0.6rem",
            }}>
              <div className="font-heading" style={{ fontSize: fs("0.78rem","0.58rem"), color: "#555", marginBottom: "0.35rem" }}>
                СЧЁТ
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                {[...players]
                  .sort((a,b) => (result.scores?.[b.name] || 0) - (result.scores?.[a.name] || 0))
                  .map((p, i) => (
                    <div key={p.name} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                      {i === 0 && <Crown style={{ color: "#ffe600", width: fs("0.9rem","0.7rem"), height: fs("0.9rem","0.7rem"), flexShrink: 0 }} />}
                      {i !== 0 && <span style={{ width: fs("0.9rem","0.7rem"), flexShrink: 0 }} />}
                      <div style={{
                        width: "0.6rem", height: "0.6rem", borderRadius: "50%",
                        background: p.color, border: "1.5px solid #000", flexShrink: 0,
                      }} />
                      <span className="font-heading" style={{ color: p.color, fontSize: fs("0.85rem","0.65rem"), flex: 1 }}>{p.name}</span>
                      <span className="font-display" style={{ color: "#fff", fontSize: fs("1rem","0.75rem") }}>
                        {result.scores?.[p.name] || 0}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}